import { Container } from '@hg/stylizator/components'
import { Text } from '@hg/stylizator/typography'
import { Spacing, desktop } from '@hg/stylizator/utils'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import parse from 'html-react-parser'
import * as React from 'react'
import styled from 'styled-components'

import BackToBlog from 'app/components/blog/BackToBlog'
import BottomCTA from 'app/components/blog/BottomCTA'
import PostMeta from 'app/components/blog/PostMeta'
import PromoBannerNoImage from 'app/components/blog/PromoBanner/PromoBannerNoImage'
import RelatedPosts from 'app/components/blog/RelatedPosts'
import Separator from 'app/components/blog/Separator'
import ShareButton from 'app/components/blog/ShareButton'
import { tocStripOptions } from 'app/components/blog/TableOfContents'
import ClientOnly from 'app/components/ClientOnly'
import { blogRegistry, createCustomRegistryBlockGetter } from 'app/components/CustomBlocks/registry'
import Pill from 'app/components/Pill'
import Tag from 'app/components/Tag'
import { ReactBlocks } from 'app/helpers/Blocks'
import { usePromoBannerData } from 'app/hooks/use-promo-banner-data'
import { useTranslation } from 'app/hooks/use-translation'
import SingleColumnLayout from 'app/layouts/SingleColumnLayout'
import { TPageData } from 'app/service/api/theme'

dayjs.extend(localizedFormat)

interface IProps {
  pageData: TPageData<'post'>
}

const PostPage: React.FC<IProps> = ({ pageData }) => {
  const t = useTranslation()
  const promoBannerData = usePromoBannerData()

  const tableOfContents = parse(pageData.table_of_contents, tocStripOptions)

  const thumbnailImage = parse(pageData.thumbnail, { trim: true }) as React.ReactElement
  const { height, width, ...thumbnailProps } = thumbnailImage?.props ?? {}

  const handleScroll = () => {
    const links = document.querySelectorAll<HTMLElement>('#toc li')
    const anchors = document.querySelectorAll<HTMLElement>('span.ez-toc-section')

    const scrollTop = window.scrollY
    const offsetTop = 300

    links.forEach((link) => {
      if (link.classList.contains('active')) {
        link.classList.add('read')
      }

      link.classList.remove('active')
    })

    for (let i = anchors.length - 1; i >= 0; i--) {
      if (scrollTop > anchors[i].offsetTop - offsetTop) {
        links[i].classList.add('active')
        break
      }
    }
  }

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const getCustomBlock = createCustomRegistryBlockGetter(blogRegistry)

  return (
    <SingleColumnLayout>
      <BackToBlog />

      <Background>
        <Container
          margins={{
            mobile: 0,
            tablet: 0,
            desktop: 0,
            largeDesktop: 0,
          }}
          style={{ padding: '0 16px', boxSizing: 'border-box' }}
        >
          <BlogLayout>
            <Main>
              <Tags>
                {pageData.categories.map((category) => (
                  <Tag key={category.url} href={category.url}>
                    {category.name}
                  </Tag>
                ))}
              </Tags>

              <Headline>
                <Text type="heading_large" useCompact>
                  <h1>{pageData.title}</h1>
                </Text>
              </Headline>

              <Thumbnail {...thumbnailProps} width="100%" height="auto" />

              <PostHeader>
                <PostMeta
                  author={pageData.author}
                  createdAt={pageData.created_at}
                  updatedAt={pageData.updated_at}
                  readingTime={pageData.reading_time_min}
                />
                <ClientOnly>
                  <ShareButton title={pageData.title} permalink={pageData.permalink} />
                </ClientOnly>
              </PostHeader>

              <PostSeparator />

              <Post>
                <React.Suspense>
                  <ReactBlocks innerBlocks={pageData.blocks} renderCustomBlock={getCustomBlock} withoutClientOnly />
                </React.Suspense>
              </Post>

              <PostFooter>
                <PostMeta
                  author={pageData.author}
                  createdAt={pageData.created_at}
                  updatedAt={pageData.updated_at}
                  readingTime={pageData.reading_time_min}
                />
                <ClientOnly>
                  <ShareButton title={pageData.title} permalink={pageData.permalink} />
                </ClientOnly>
              </PostFooter>

              <Pills>
                {pageData.categories.map((category) => (
                  <Pill key={category.url} href={category.url}>
                    {category.name}
                  </Pill>
                ))}
              </Pills>
            </Main>

            <Sidebar>
              {tableOfContents}

              <Spacing top={8} bottom={3}>
                <PromoBannerNoImage {...promoBannerData} variation="small" />
              </Spacing>
            </Sidebar>
          </BlogLayout>
        </Container>
      </Background>

      <RelatedPosts posts={pageData.related_posts} title={t('related_articles')} />

      <TvSofaImage src="/assets/illustrations/tv-sofa.svg" />

      <BottomCTA />
    </SingleColumnLayout>
  )
}

const Background = styled.div`
  background: ${(props) => props.theme.color.blue[5]};
`

const Main = styled.main`
  flex: 1;
  max-width: 708px;
  width: 100%;
`

const Post = styled.div``

const Headline = styled.div`
  margin-top: 8px;

  @media ${desktop} {
    margin-top: 16px;
  }
`

const Thumbnail = styled.img`
  display: block;
  margin-top: 24px;
  border-radius: 8px;

  @media ${desktop} {
    margin-top: 48px;
  }
`

const Sidebar = styled.aside`
  display: none;

  @media ${desktop} {
    position: sticky;
    top: 80px;
    align-self: flex-start;
    display: grid;
    grid-auto-rows: auto;
    flex: 1;
    width: 100%;
    max-width: 300px;
    height: calc(100vh - 80px);
  }
`

const BlogLayout = styled.div`
  display: flex;
  gap: 88px;
  padding: 32px 0 56px;

  @media ${desktop} {
    padding: 56px 0 112px;
  }
`

const PostHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 32px;

  @media ${desktop} {
    margin-top: 40px;
  }
`

const PostFooter = styled(PostHeader)`
  clear: both;
  float: left;
  width: 100%;
  margin-top: 48px;

  @media ${desktop} {
    margin-top: 80px;
  }
`

const PostSeparator = styled(Separator)`
  margin: 24px 0 24px;

  @media ${desktop} {
    margin-bottom: 32px;
  }
`

const Tags = styled.div`
  display: flex;
  gap: 8px;
`

const Pills = styled.div`
  clear: both;
  float: left;
  width: 100%;
  display: flex;
  gap: 8px;
  margin: 32px 0 56px;

  @media ${desktop} {
    margin-top: 56px 0 112px;
  }
`

const TvSofaImage = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  height: 48vw;
  padding-top: 96px;
  background-color: ${(props) => props.theme.color.blue[5]};

  @media ${desktop} {
    height: auto;
  }
`

export default PostPage
